/**
 * KMPulseWidget — compact dashboard card that surfaces Knowledge Management
 * maturity signal right on the home dashboard so users don't have to dig into
 * `/tests`. Three modes:
 *
 *   1. No submission yet → "take the 5-min KM assessment" CTA.
 *   2. At least one submission → personal latest score + maturity badge
 *      + delta-vs-previous arrow + top AI recommendation.
 *   3. Admin/HR users → additionally an org-wide strip showing total
 *      submissions + team avg with a deep-link to the full Analytics view.
 *
 * Self-contained: fetches its own data so the host dashboard only needs to
 * render `<KMPulseWidget />` (zero props).
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Target, TrendingUp, TrendingDown, ArrowRight, Sparkles, Loader2, Users, Award } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MATURITY_BG = {
  Advanced: 'bg-[#34C759] text-white',
  Intermediate: 'bg-[#007AFF] text-white',
  Developing: 'bg-[#FF9500] text-white',
  Initial: 'bg-[#FF3B30] text-white',
};

const scoreColor = (s) => (s >= 80 ? '#34C759' : s >= 60 ? '#FF9500' : '#FF3B30');

const KMPulseWidget = () => {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submissions, setSubmissions] = useState([]);
  const [analytics, setAnalytics] = useState(null);

  const isPrivileged = user?.role === 'admin' || user?.role === 'hr';

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      const headers = { Authorization: `Bearer ${token}` };
      try {
        const mineReq = axios.get(`${API}/assessment-submissions/my`, { headers });
        const orgReq = isPrivileged
          ? axios.get(`${API}/assessment-analytics`, { headers, params: { email_domain: '' } }).catch(() => null)
          : Promise.resolve(null);
        const [mineRes, orgRes] = await Promise.all([mineReq, orgReq]);
        if (cancelled) return;
        setSubmissions(mineRes.data || []);
        if (orgRes) setAnalytics(orgRes.data);
      } catch {
        // widget is non-critical — leave empty state on failure
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, isPrivileged]);

  if (loading) {
    return (
      <Card className="border-[#E5E7EB]" data-testid="km-pulse-widget">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-[#002FA7]" />
        </CardContent>
      </Card>
    );
  }

  // Empty state — user has never taken the assessment.
  if (submissions.length === 0) {
    return (
      <Card className="border-[#E5E7EB] border-l-4 border-l-[#002FA7]" data-testid="km-pulse-widget">
        <CardContent className="p-5 flex items-center gap-4 flex-wrap">
          <div className="w-12 h-12 rounded-xl bg-[#002FA7]/10 flex items-center justify-center flex-shrink-0">
            <Target className="w-6 h-6 text-[#002FA7]" />
          </div>
          <div className="flex-1 min-w-[200px]">
            <h3 className="text-sm font-semibold text-[#0A0A0A]">How does your KM maturity stack up?</h3>
            <p className="text-xs text-[#6B7280] mt-0.5">
              Take the 5-minute Capability Assessment to see your People / Process / Technology score and benchmark it against industry peers.
            </p>
          </div>
          <Link to="/tests">
            <Button size="sm" className="bg-[#002FA7] hover:bg-[#00227A]" data-testid="km-pulse-empty-cta">
              Start assessment<ArrowRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </Link>
        </CardContent>
      </Card>
    );
  }

  // Rendered state — at least one submission.
  const latest = submissions[0];
  const previous = submissions[1];
  const delta = previous ? latest.overall_score - previous.overall_score : null;

  // Pick the first recommendation across all areas (AI-generated during submit).
  const recs = latest.recommendations;
  const firstRec = recs?.areas?.[0]?.recommendations?.[0];

  // Pillar scores (breakdown stored as quick_km/checklist/technology in backend).
  const bd = latest.breakdown || {};
  const pillars = [
    { label: 'Process', pct: bd.quick_km?.pct, color: '#002FA7' },
    { label: 'People', pct: bd.checklist?.pct, color: '#34C759' },
    { label: 'Tech', pct: bd.technology?.pct, color: '#FF9500' },
  ].filter(p => typeof p.pct === 'number');

  return (
    <Card className="border-[#E5E7EB]" data-testid="km-pulse-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Target className="w-4 h-4 text-[#002FA7]" />KM Pulse
            <Badge variant="outline" className="text-[9px] ml-1">{submissions.length} submission{submissions.length === 1 ? '' : 's'}</Badge>
          </CardTitle>
          <Link to="/tests" className="text-[11px] text-[#002FA7] hover:underline flex items-center gap-0.5" data-testid="km-pulse-view-all">
            View all <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </CardHeader>
      <CardContent className="pt-0 space-y-3">
        {/* Personal score row */}
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-full bg-[#002FA7]/10 flex items-center justify-center" data-testid="km-pulse-score">
              <span className="text-lg font-bold" style={{ color: scoreColor(latest.overall_score) }}>
                {latest.overall_score}%
              </span>
            </div>
            <div>
              <Badge className={`text-[10px] ${MATURITY_BG[latest.maturity_level] || 'bg-[#9CA3AF]'}`} data-testid="km-pulse-maturity">
                {latest.maturity_level}
              </Badge>
              <p className="text-[10px] text-[#9CA3AF] mt-1">
                {format(new Date(latest.created_at), 'MMM d, yyyy')}
                {delta !== null && (
                  <span
                    className={`ml-1.5 font-semibold inline-flex items-center gap-0.5 ${delta > 0 ? 'text-[#34C759]' : delta < 0 ? 'text-[#FF3B30]' : 'text-[#9CA3AF]'}`}
                    data-testid="km-pulse-delta"
                    title={`Previous: ${previous.overall_score}%`}
                  >
                    {delta > 0 ? <TrendingUp className="w-3 h-3" /> : delta < 0 ? <TrendingDown className="w-3 h-3" /> : null}
                    {delta > 0 ? '+' : ''}{delta}pts
                  </span>
                )}
              </p>
            </div>
          </div>

          {/* Pillar bars */}
          {pillars.length > 0 && (
            <div className="flex gap-3 ml-auto flex-wrap" data-testid="km-pulse-pillars">
              {pillars.map(p => (
                <div key={p.label} className="min-w-[72px]">
                  <div className="flex items-center justify-between text-[10px] mb-0.5">
                    <span className="text-[#6B7280]">{p.label}</span>
                    <span className="font-semibold" style={{ color: p.color }}>{p.pct}%</span>
                  </div>
                  <div className="h-1.5 w-[72px] rounded-full bg-[#F3F4F6] overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${p.pct}%`, backgroundColor: p.color }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Top recommendation */}
        {firstRec && (
          <div className="p-2.5 rounded-lg bg-[#FAFAFA] border-l-[3px] border-[#FF9500]" data-testid="km-pulse-top-rec">
            <p className="text-[9px] uppercase tracking-wider font-semibold text-[#FF9500] mb-1 flex items-center gap-1">
              <Sparkles className="w-3 h-3" />Top recommendation
            </p>
            <p className="text-[11px] text-[#0A0A0A] leading-relaxed">{firstRec.text}</p>
          </div>
        )}

        {/* Admin/HR org strip */}
        {isPrivileged && analytics && (
          <div className="pt-3 border-t border-[#F3F4F6] flex items-center gap-4 flex-wrap" data-testid="km-pulse-org-strip">
            <div className="flex items-center gap-2">
              <Users className="w-3.5 h-3.5 text-[#6B7280]" />
              <span className="text-[11px] text-[#6B7280]">Team</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-lg font-bold text-[#0A0A0A]" data-testid="km-pulse-team-avg">{analytics.avg_score}%</span>
              <span className="text-[10px] text-[#9CA3AF]">avg</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-sm font-semibold text-[#002FA7]">{analytics.total_submissions}</span>
              <span className="text-[10px] text-[#9CA3AF]">submissions</span>
            </div>
            {analytics.leaderboard?.[0] && (
              <div className="flex items-center gap-1 ml-auto" title={`Top: ${analytics.leaderboard[0].user_name}`}>
                <Award className="w-3.5 h-3.5 text-[#FF9500]" />
                <span className="text-[10px] text-[#6B7280]">
                  Top: <span className="font-semibold text-[#0A0A0A]">{analytics.leaderboard[0].overall_score}%</span>
                </span>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default KMPulseWidget;
